/**
 * MasterDetailHeader
 *
 * Page header for a list/detail screen — breadcrumb on the left (last crumb is the
 * page title, earlier crumbs are router links separated by '›'), a right-aligned
 * actions slot, and an optional inline busy spinner for background refetches.
 * See `.github/context/list-grid-standard.context.md` §4.
 *
 * Drive `busy` with `isFetching && !isLoading` — the inline refetch indicator, NOT the
 * first-load spinner (that is the loading skeleton's job).
 */
import type { FC, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Caption1, Spinner, Title2, makeStyles, tokens } from '@fluentui/react-components';

export interface Crumb {
  label: string;
  /** Omit on the final crumb — it renders as the page title, not a link. */
  href?: string;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalL,
  },
  titleGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  crumbLink: {
    color: tokens.colorBrandForegroundLink,
    textDecorationLine: 'none',
    ':hover': {
      textDecorationLine: 'underline',
    },
  },
  separator: {
    color: tokens.colorNeutralForeground3,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
  },
});

export interface MasterDetailHeaderProps {
  crumbs: Crumb[];
  actions?: ReactNode;
  busy?: boolean;
  /**
   * Optional supporting copy rendered under the title (`Caption1`, token-styled).
   * Gives pages a path to adopt `MasterDetailHeader` instead of keeping the app's
   * `PageHeader` outside the frame just to get a subtitle line. Omit for no change
   * in behaviour.
   */
  subtitle?: ReactNode;
}

export const MasterDetailHeader: FC<MasterDetailHeaderProps> = ({ crumbs, actions, busy, subtitle }) => {
  const styles = useStyles();
  const lastIndex = crumbs.length - 1;

  return (
    <div className={styles.root}>
      <div className={styles.titleGroup}>
        <div className={styles.breadcrumb}>
          {crumbs.map((crumb, index) => {
            const isLast = index === lastIndex;
            return (
              <span key={`${crumb.label}-${index}`} className={styles.breadcrumb}>
                {isLast ? (
                  <Title2>{crumb.label}</Title2>
                ) : (
                  <>
                    {crumb.href ? (
                      <Link to={crumb.href} className={styles.crumbLink}>
                        {crumb.label}
                      </Link>
                    ) : (
                      <span>{crumb.label}</span>
                    )}
                    <span className={styles.separator}>›</span>
                  </>
                )}
              </span>
            );
          })}
          {busy && <Spinner size="tiny" data-testid="grid-loading" />}
        </div>
        {subtitle && <Caption1 className={styles.subtitle}>{subtitle}</Caption1>}
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </div>
  );
};
